import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { Eye, EyeOff, ArrowRight, Loader2, Copy, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { setDemoMode, clearDemoMode } from '../../contexts/AppModeContext';
import * as ediApi from '../../lib/api/ediApi';
import StripeSignupCheckout from '../../components/auth/StripeSignupCheckout';
import RevqdemoMetricsPanel from '../../components/auth/RevqdemoMetricsPanel';

const ARIAONE_LOGO = "/ariaone_logo_black.png";

/**
 * Extract a human-readable error message from a vendor error response.
 * The vendor uses three different shapes:
 *   - string detail:    {"detail": "Invalid email or password..."}
 *   - object detail:    {"detail": {"message": "...", "issues": ["...", "..."]}}
 *   - FastAPI array:    {"detail": [{"msg": "field required", "loc": [...]}]}
 * Falls back to data.message, then the provided default.
 */
function readVendorError(data, fallback) {
  const d = data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d.length) {
    const msgs = d.map((e) => e?.msg || e?.message).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (d && typeof d === 'object') {
    const issues = Array.isArray(d.issues) ? d.issues.filter(Boolean) : [];
    if (d.message && issues.length) return `${d.message}: ${issues.join('; ')}`;
    if (d.message) return d.message;
    if (issues.length) return issues.join('; ');
  }
  if (typeof data?.message === 'string' && data.message) return data.message;
  return fallback;
}


const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const isDemo = params.get('demo') === 'true';
  const { login, register } = useAuth();

  const [tab, setTab] = useState(location.pathname.includes('signup') ? 'create' : 'signin');
  // /revqdemo/login (preferred) and /apidemo/login (kept for backward compat,
  // currently shadowed by the ingress /api routing rule) are the SSO-less variants.
  const isRevqDemo = location.pathname.startsWith('/revqdemo')
    || location.pathname.startsWith('/apidemo')
    || location.pathname.startsWith('/edi');
  const hideSSO = isRevqDemo;
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState(isDemo ? 'Sarah' : '');
  const [lastName, setLastName] = useState(isDemo ? 'Chen' : '');
  const [email, setEmail] = useState(isDemo ? 'dr.sarah.chen@westsidemd.com' : '');
  const [password, setPassword] = useState(isDemo ? 'Str0ng!Pass2026' : '');
  const [confirm, setConfirm] = useState(isDemo ? 'Str0ng!Pass2026' : '');
  const [loginEmail, setLoginEmail] = useState(isDemo ? 'dr.sarah.chen@westsidemd.com' : '');
  const [loginPw, setLoginPw] = useState('');

  // /revqdemo state — Stripe checkout overlay + post-auth JWT panel.
  const [checkout, setCheckout] = useState(null);   // { publishableKey, clientSecret, sessionId }
  const [authResult, setAuthResult] = useState(null); // { access_token, raw, source }
  const [copied, setCopied] = useState(false);
  const [finalizing, setFinalizing] = useState(false); // true while we're polling /signup/finalize after a Stripe redirect-return

  // Stripe redirect-return handler — when the vendor's hardcoded return_url
  // brings the user back here with ?setup_session_id=cs_..., poll the finalize
  // endpoint and land on the JWT panel.
  useEffect(() => {
    const sid = params.get('setup_session_id');
    if (!isRevqDemo || !sid || authResult || finalizing) return undefined;

    let cancelled = false;
    let attempts = 0;
    const MAX = 30;

    // Defer all setState into the next macrotask so the eslint
    // react-hooks/set-state-in-effect rule sees them as async, not as
    // inline effect-body mutations.
    const setStateAsync = (fn) => { setTimeout(fn, 0); };
    setStateAsync(() => setFinalizing(true));

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const { ok, status, data } = await ediApi.signupFinalize(sid);
      if (cancelled) return;
      const vendorStatus = data?.status;

      if (ok && (vendorStatus === 'succeeded' || data?.access_token)) {
        try { localStorage.setItem('edi_token', data.access_token); } catch { /* storage off */ }
        setStateAsync(() => {
          setAuthResult({ access_token: data.access_token, raw: data, source: 'login' });
          setFinalizing(false);
        });
        return;
      }
      if (vendorStatus === 'cancelled' || vendorStatus === 'expired' || status === 410) {
        setStateAsync(() => {
          setFinalizing(false);
          setError(`Signup ${vendorStatus || 'abandoned'}. Please try again.`);
        });
        return;
      }
      if (attempts >= MAX) {
        setStateAsync(() => {
          setFinalizing(false);
          setError('Finalize timeout — please sign in to continue.');
        });
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => { cancelled = true; };
  }, []);

  // No URL→state sync useEffects needed: each route remounts AuthPage so
  // the initial useState calls above handle both `isDemo` and the active tab.

  const strength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthColors = ['#ef4444','#f59e0b','#3b82f6','#22c55e'];
  const strengthLabels = ['Weak','Fair','Good','Strong'];

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isRevqDemo) {
        const { ok, status, data } = await ediApi.login({ email: loginEmail, password: loginPw });
        if (!ok || !data?.access_token) {
          if (status === 429) {
            throw new Error(readVendorError(data, 'Too many attempts — please wait a minute and retry.'));
          }
          throw new Error(readVendorError(data, `Sign-in failed (HTTP ${status})`));
        }
        try { localStorage.setItem('edi_token', data.access_token); } catch { /* storage off */ }
        setAuthResult({ access_token: data.access_token, raw: data, source: 'login' });
      } else {
        clearDemoMode();
        await login(loginEmail, loginPw);
        navigate('/landing');
      }
    } catch (err) {
      setError(err.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (isRevqDemo) {
      setSubmitting(true);
      try {
        const { ok, status, data } = await ediApi.signupIntent({
          email, password, first_name: firstName, last_name: lastName,
          origin_url: window.location.origin,
        });
        if (!ok || !data?.client_secret || !data?.session_id || !data?.publishable_key) {
          throw new Error(readVendorError(data, `Signup intent failed (HTTP ${status})`));
        }
        setCheckout({
          publishableKey: data.publishable_key,
          clientSecret: data.client_secret,
          sessionId: data.session_id,
        });
      } catch (err) {
        setError(err.message || 'Signup failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (isDemo) { setDemoMode(true); navigate('/mfa'); return; }
    setSubmitting(true);
    try {
      await register({ email, password, name: `${firstName} ${lastName}`.trim(), first_name: firstName, last_name: lastName });
      navigate('/mfa');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripeSuccess = async ({ access_token: signupToken, raw }) => {
    setCheckout(null);

    // The signup-finalize endpoint already returns a usable JWT, but the user
    // expects to land in the exact same post-login state — so re-authenticate
    // via /login. This also runs whatever side effects the vendor attaches to
    // a real login (audit log, last_login_at, etc.).
    try {
      const { ok, data } = await ediApi.login({ email, password });
      if (ok && data?.access_token) {
        try { localStorage.setItem('edi_token', data.access_token); } catch { /* storage off */ }
        setAuthResult({ access_token: data.access_token, raw: data, source: 'login' });
        return;
      }
    } catch { /* fall through to signup token */ }

    // Fallback: vendor isn't ready yet, or login briefly failed. Use the
    // signup-issued token so the user still lands on the JWT panel.
    try { localStorage.setItem('edi_token', signupToken); } catch { /* storage off */ }
    setAuthResult({ access_token: signupToken, raw, source: 'login' });
  };

  const handleCopyToken = async () => {
    if (!authResult?.access_token) return;
    try {
      await navigator.clipboard.writeText(authResult.access_token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4" data-testid="auth-page">
      {checkout && (
        <StripeSignupCheckout
          publishableKey={checkout.publishableKey}
          clientSecret={checkout.clientSecret}
          sessionId={checkout.sessionId}
          onSuccess={handleStripeSuccess}
          onError={(err) => { setCheckout(null); setError(err?.message || 'Stripe checkout failed'); }}
          onCancel={() => setCheckout(null)}
        />
      )}

      <div className="w-full max-w-[420px] space-y-8">
        {/* Logo */}
        <div className="text-center">
          <img src={ARIAONE_LOGO} alt="AriaOne" className="h-8 mx-auto mb-2" data-testid="auth-logo" />
          <p className="text-xs text-slate-400 tracking-wider font-medium">HEALTHCARE PLATFORM</p>
        </div>

        {/* /revqdemo-specific label — appears only on the credit-card signup test page. */}
        {location.pathname.startsWith('/revqdemo') && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-center"
            data-testid="revqdemo-label"
          >
            <p className="text-[12px] font-medium text-amber-900">
              This is the page to test new users created via CC
            </p>
          </div>
        )}

        {/* /revqdemo JWT confirmation panel — replaces the sign-in form on success */}
        {isRevqDemo && finalizing && !authResult && (
          <div
            className="border border-slate-200 rounded-2xl p-6 bg-white shadow-sm text-center"
            data-testid="revqdemo-finalizing"
          >
            <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-900">Finalizing your tenant…</p>
            <p className="text-[12px] text-slate-500 mt-1">
              Card validated. Creating your account and signing you in.
            </p>
          </div>
        )}

        {isRevqDemo && authResult && (
          <div
            className="border border-slate-200 rounded-2xl p-6 bg-white shadow-sm space-y-4"
            data-testid="revqdemo-auth-result"
          >
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <p className="text-sm font-semibold text-slate-900">Authenticated</p>
            </div>
            <p className="text-[12px] text-slate-500">
              8-hour Bearer JWT — use as <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">Authorization: Bearer …</code> on any /api/edi or /api/ehibridge call.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <textarea
                readOnly
                value={authResult.access_token}
                className="w-full text-[10px] font-mono text-slate-700 bg-transparent border-0 resize-none focus:outline-none"
                rows={4}
                data-testid="revqdemo-access-token"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyToken}
                className="gap-1.5 text-xs"
                data-testid="revqdemo-copy-token"
              >
                <Copy className="h-3 w-3" />
                {copied ? 'Copied' : 'Copy token'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setAuthResult(null); setLoginEmail(''); setLoginPw(''); }}
                className="text-xs"
                data-testid="revqdemo-sign-out"
              >
                Sign out
              </Button>
            </div>
            {/* Next-logical-step reveal: fetches tenant snapshot metrics. */}
            <div className="pt-3 border-t border-slate-100">
              <RevqdemoMetricsPanel token={authResult.access_token} />
            </div>
          </div>
        )}

        {/* Card — hidden once /revqdemo authentication succeeds OR mid-finalize */}
        {!(isRevqDemo && (authResult || finalizing)) && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {/* Tab Toggle */}
          <div className="flex rounded-lg bg-slate-100 p-1 mb-6" data-testid="auth-tab-toggle">
            <button onClick={() => setTab('signin')}
              className={cn("flex-1 py-2 text-sm font-medium rounded-md transition-all",
                tab === 'signin' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
              data-testid="tab-signin">
              Sign in
            </button>
            <button onClick={() => setTab('create')}
              className={cn("flex-1 py-2 text-sm font-medium rounded-md transition-all",
                tab === 'create' ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
              data-testid="tab-create">
              Create account
            </button>
          </div>

          {isDemo && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <div className="h-1.5 w-1.5 rounded-full bg-slate-600 animate-pulse" />
              <span className="text-[11px] text-slate-600 font-medium">Demo mode — fields pre-filled</span>
            </div>
          )}

          {error && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="auth-error">
              <p className="text-[12px] text-red-600">{error}</p>
            </div>
          )}

          {tab === 'create' ? (
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] text-slate-500 mb-1 block">First name</label>
                  <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Sarah"
                    className="h-11 text-sm border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-first-name" />
                </div>
                <div>
                  <label className="text-[12px] text-slate-500 mb-1 block">Last name</label>
                  <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Chen"
                    className="h-11 text-sm border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-last-name" />
                </div>
              </div>
              <div>
                <label className="text-[12px] text-slate-500 mb-1 block">Email address</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="dr.sarah.chen@westsidemd.com"
                  className="h-11 text-sm border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-email" />
              </div>
              <div>
                <label className="text-[12px] text-slate-500 mb-1 block">Password</label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    className="h-11 text-sm pr-10 border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password && (
                  <div className="flex gap-1 mt-2">
                    {[0,1,2,3].map(i => (
                      <div key={i} className="flex-1 h-1 rounded-full transition-colors" style={{ background: i < strength ? strengthColors[strength - 1] : '#e2e8f0' }} />
                    ))}
                    <span className="text-[10px] ml-2" style={{ color: strengthColors[strength - 1] || '#94a3b8' }}>{strengthLabels[strength - 1] || ''}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[12px] text-slate-500 mb-1 block">Confirm password</label>
                <div className="relative">
                  <Input type={showPw2 ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
                    className="h-11 text-sm pr-10 border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-confirm" />
                  <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 text-sm font-medium bg-slate-900 hover:bg-slate-800" data-testid="create-account-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create my account <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
              {!hideSSO && (
                <>
                  <div className="flex items-center gap-3"><div className="flex-1 h-px bg-slate-200" /><span className="text-slate-400 text-[12px]">or</span><div className="flex-1 h-px bg-slate-200" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" className="h-11 text-sm border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="sso-google">Google</Button>
                    <Button variant="outline" className="h-11 text-sm border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="sso-microsoft">Microsoft</Button>
                  </div>
                </>
              )}
              <p className="text-center text-[13px] text-slate-500">Already have an account? <button type="button" onClick={() => setTab('signin')} className="text-slate-900 hover:underline font-medium">Sign in</button></p>
            </form>
          ) : (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="text-[12px] text-slate-500 mb-1 block">Email address</label>
                <Input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="dr.sarah.chen@westsidemd.com"
                  className="h-11 text-sm border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-login-email" />
              </div>
              <div>
                <label className="text-[12px] text-slate-500 mb-1 block">Password</label>
                <Input type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)} placeholder="Enter your password"
                  className="h-11 text-sm border-slate-200 focus-visible:ring-violet-500 focus-visible:border-violet-500" data-testid="input-login-password" />
                <div className="text-right mt-1"><button type="button" className="text-[12px] text-slate-500 hover:underline">Forgot password?</button></div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11 text-sm font-medium bg-slate-900 hover:bg-slate-800" data-testid="sign-in-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
              {!hideSSO && (
                <>
                  <div className="flex items-center gap-3"><div className="flex-1 h-px bg-slate-200" /><span className="text-slate-400 text-[12px]">or</span><div className="flex-1 h-px bg-slate-200" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" className="h-11 text-sm border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="sso-google">Google</Button>
                    <Button variant="outline" className="h-11 text-sm border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="sso-microsoft">Microsoft</Button>
                  </div>
                </>
              )}
              <p className="text-center text-[13px] text-slate-500">New to Aria One? <button type="button" onClick={() => setTab('create')} className="text-slate-900 hover:underline font-medium">Create an account</button></p>
            </form>
          )}
        </div>
        )}

        {/* Quick Demo Access — feature-flag gated.
            Hidden by default. To re-enable, set in `frontend/.env`:
              REACT_APP_SHOW_DEMO_ACCESS=true
            then restart the frontend. */}
        {String(process.env.REACT_APP_SHOW_DEMO_ACCESS || '').toLowerCase() === 'true' && (
          <div className="space-y-3">
            <p className="text-[11px] text-slate-400 text-center uppercase tracking-wider font-medium">Quick demo access</p>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => navigate('/signup?demo=true')} variant="outline"
                className="h-11 text-sm font-medium border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
                data-testid="demo-create-account-btn">
                Create Account
              </Button>
              <Button onClick={() => { setDemoMode(true); navigate('/landing'); }}
                className="h-11 text-sm font-medium bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="demo-existing-user-btn">
                Existing User
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-[11px] text-slate-400">By continuing, you agree to our Terms of Service and Privacy Policy.</p>
      </div>
    </div>
  );
};

export default AuthPage;
